import { useCallback, useEffect, useRef, useState } from 'react';
import { usePaymentGateway } from '../../../hooks/usePaymentGateway';
import {
  INITIAL_CHECK_DELAY_MS,
  POLL_INTERVAL_MS,
  STATUS_PAGO,
  STATUS_PROCESSANDO,
} from '../constants';

type UseGetnet3DSPaymentArgs = {
  onSuccess: () => void;
  onError: () => void;
};

export function useGetnet3DSPayment({ onSuccess, onError }: UseGetnet3DSPaymentArgs) {
  const { verificaStatusPagamentoCartao } = usePaymentGateway();
  const [isPolling, setIsPolling] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRunningRef = useRef(false);

  const clearPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isRunningRef.current = false;
    setIsPolling(false);
  }, []);

  const runPostChallengeFlow = useCallback(
    (transactionId: string) => {
      if (isRunningRef.current) {return;}
      isRunningRef.current = true;
      setIsPolling(true);

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;

        verificaStatusPagamentoCartao({ ds_trans_id: transactionId })
          .then(({ data }) => {
            if (data.status === STATUS_PAGO) {
              clearPolling();
              onSuccess();
              return;
            }

            if (data.status === STATUS_PROCESSANDO) {
              intervalRef.current = setInterval(() => {
                verificaStatusPagamentoCartao({ ds_trans_id: transactionId })
                  .then(({ data: res }) => {
                    if (res.status === STATUS_PAGO) {
                      clearPolling();
                      onSuccess();
                    }
                  })
                  .catch(() => {
                    clearPolling();
                    onError();
                  });
              }, POLL_INTERVAL_MS);
            }
          })
          .catch(() => {
            clearPolling();
            onError();
          });
      }, INITIAL_CHECK_DELAY_MS);
    },
    [verificaStatusPagamentoCartao, onSuccess, onError, clearPolling],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {clearTimeout(timeoutRef.current);}
      if (intervalRef.current) {clearInterval(intervalRef.current);}
      isRunningRef.current = false;
    };
  }, []);

  return { runPostChallengeFlow, isPolling, clearPolling };
}
